import { useState, useEffect, useMemo } from 'preact/hooks'
import { X, Save, Info } from 'lucide-preact'
import { userRolesApi } from '../../api/userRoles'
import { RoleSelector } from '../common/RoleSelector'
import { PersonAvatar } from '../common/Avatar'
import { AllCustomersAccessSection } from './AllCustomersAccessSection'
import { useAllCustomersAccess } from './useAllCustomersAccess'
import { useToast } from '@/hooks/useToast'
import type {
  UserProfileWithRolesDto,
  UserCompanyRoleSetUpdateDto,
  WorkspaceRolesUpdateDto,
} from '../../types/userRoles'
import type { ClientWorkspaceViewDto } from '../../types/company'

interface UserRoleEditModalProps {
  isOpen: boolean
  user: UserProfileWithRolesDto
  // Id and Name are all this reads, and callers hand it both full workspaces and summaries —
  // neither of which is a subtype of the other now that the summary carries ClientCompanyName.
  companyWorkspaces: Pick<ClientWorkspaceViewDto, 'Id' | 'Name'>[]
  allUsers: UserProfileWithRolesDto[]
  onClose: () => void
  onSuccess: (updatedUser: UserProfileWithRolesDto) => void
  /**
   * ORB-1019: set only on the managing company's own Users page, for its admins editing one of its own
   * people. Shows the "Access to all customers" switch.
   */
  allCustomersAccess?: { managingCompanyId: string; managingCompanyName: string }
}

export function UserRoleEditModal({
  isOpen,
  user,
  companyWorkspaces,
  allUsers,
  onClose,
  onSuccess,
  allCustomersAccess
}: UserRoleEditModalProps) {
  const { showSuccess, showWarning } = useToast()
  const switchAccess = useAllCustomersAccess(
    allCustomersAccess ? { ...allCustomersAccess, userProfileId: user.UserProfileId } : null,
    isOpen
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [collapsedWorkspaces, setCollapsedWorkspaces] = useState<Set<string>>(new Set())

  // A role set saved while the modal stayed open, because the switch save after it failed. Its ETag is
  // the current one, so a retry must build on it rather than on the `user` the modal was opened with.
  const [savedRoleSet, setSavedRoleSet] = useState<UserProfileWithRolesDto['UserCompanyRoleSet']>(null)
  const current: UserProfileWithRolesDto = savedRoleSet ? { ...user, UserCompanyRoleSet: savedRoleSet } : user

  // Role state
  const [companyRoles, setCompanyRoles] = useState<string[]>([])
  const [workspaceRoles, setWorkspaceRoles] = useState<Record<string, string[]>>({})

  // Track original state for change detection
  const [originalCompanyRoles, setOriginalCompanyRoles] = useState<string[]>([])
  const [originalWorkspaceRoles, setOriginalWorkspaceRoles] = useState<Record<string, string[]>>({})

  // Derive selectedWorkspaces from workspaceRoles
  const selectedWorkspaces = useMemo(
    () => Object.keys(workspaceRoles).filter(wsId => (workspaceRoles[wsId] || []).length > 0),
    [workspaceRoles]
  )

  // Initialize form data when modal opens or user changes
  useEffect(() => {
    if (!isOpen || !user) return
    const roleSet = user.UserCompanyRoleSet

    const compRoles = (roleSet?.CompanyRoles || []).map(r => r as string)
    const wsRoles: Record<string, string[]> = {}
    const wsList = roleSet?.WorkspaceRoles || []
    wsList.forEach(ws => {
      wsRoles[ws.WorkspaceId] = (ws.Roles || []).map(r => r as string)
    })

    setSavedRoleSet(null)
    setCompanyRoles(compRoles)
    setOriginalCompanyRoles([...compRoles])
    setWorkspaceRoles(wsRoles)
    setOriginalWorkspaceRoles(JSON.parse(JSON.stringify(wsRoles)))
    setError(null)
  }, [isOpen, user])

  const handleCompanyRoleChange = (role: string, checked: boolean) => {
    // Single selection: set to just this role, or clear if deselecting
    setCompanyRoles(checked ? [role] : [])

    if (checked && role === 'Admin' && error?.includes('Admin')) {
      setError(null)
    }
  }

  const handleWorkspaceRoleChange = (workspaceId: string, role: string, checked: boolean) => {
    setWorkspaceRoles(prev => ({
      ...prev,
      // Single selection per workspace: set to just this role, or clear if deselecting
      [workspaceId]: checked ? [role] : []
    }))

    if (checked && role === 'Admin' && error?.includes('Admin')) {
      setError(null)
    }
  }

  const toggleWorkspaceCollapse = (workspaceId: string) => {
    setCollapsedWorkspaces(prev => {
      const newSet = new Set(prev)
      if (newSet.has(workspaceId)) {
        newSet.delete(workspaceId)
      } else {
        newSet.add(workspaceId)
      }
      return newSet
    })
  }

  // Check if there are any changes from the original state
  const hasChanges = (): boolean => {
    if (companyRoles.length !== originalCompanyRoles.length ||
      !companyRoles.every(role => originalCompanyRoles.includes(role))) {
      return true
    }

    const currentWsIds = Object.keys(workspaceRoles).filter(wsId => (workspaceRoles[wsId] || []).length > 0)
    const originalWsIds = Object.keys(originalWorkspaceRoles).filter(wsId => (originalWorkspaceRoles[wsId] || []).length > 0)

    if (currentWsIds.length !== originalWsIds.length ||
      !currentWsIds.every(ws => originalWsIds.includes(ws))) {
      return true
    }

    for (const wsId of [...currentWsIds, ...originalWsIds]) {
      const current = workspaceRoles[wsId] || []
      const original = originalWorkspaceRoles[wsId] || []
      if (current.length !== original.length ||
        !current.every(role => original.includes(role))) {
        return true
      }
    }

    return false
  }

  // Check if removing admin roles would leave no admins
  const validateAdminExists = (): { valid: boolean; message?: string } => {
    const currentUserWillHaveAdmin = companyRoles.includes('Admin') ||
      Object.values(workspaceRoles).some(roles => roles.includes('Admin'))

    if (currentUserWillHaveAdmin) {
      return { valid: true }
    }

    const otherUsersWithAdmin = allUsers.filter(otherUser => {
      if (otherUser.UserProfileId === user.UserProfileId) return false
      const otherUserRoles = otherUser.UserCompanyRoleSet
      if (!otherUserRoles) return false
      if (otherUserRoles.CompanyRoles?.includes('Admin')) return true
      return otherUserRoles.WorkspaceRoles?.some(wsRole =>
        wsRole.Roles.includes('Admin')
      ) || false
    })

    if (otherUsersWithAdmin.length === 0) {
      return {
        valid: false,
        message: "Cannot remove Admin role - at least one user must have Admin access to manage the company."
      }
    }

    return { valid: true }
  }

  const handleSave = async () => {
    const rolesChanged = hasChanges()
    if (!rolesChanged && switchAccess.dirty) {
      await saveSwitch(current)
      return
    }

    if (!current.UserCompanyRoleSet?.Id) {
      setError('Cannot update user roles: missing role set ID')
      return
    }

    const adminValidation = validateAdminExists()
    if (!adminValidation.valid) {
      setError(adminValidation.message!)
      return
    }

    setSaving(true)
    setError(null)

    try {
      const workspacesWithRoles = Object.keys(workspaceRoles).filter(wsId =>
        (workspaceRoles[wsId] || []).length > 0
      )

      const workspaceRoleUpdates: WorkspaceRolesUpdateDto[] = workspacesWithRoles.map(workspaceId => ({
        WorkspaceId: workspaceId,
        Roles: workspaceRoles[workspaceId] || []
      }))

      const updateDto: UserCompanyRoleSetUpdateDto = {
        ETag: current.UserCompanyRoleSet!.ETag,
        CompanyRoles: companyRoles,
        WorkspaceRoles: workspaceRoleUpdates
      }

      // ORB-306: route carries the tenant; Id is derived server-side
      const response = await userRolesApi.updateUserCompanyRoleSet(
        current.UserCompanyRoleSet!.UserProfileId,
        current.UserCompanyRoleSet!.ClientCompanyId,
        updateDto
      )

      if (response.error) {
        setError(response.error)
        return
      }

      const updatedUser: UserProfileWithRolesDto = {
        ...current,
        UserCompanyRoleSet: response.data!
      }

      if (switchAccess.dirty) {
        await saveSwitch(updatedUser)
        return
      }

      onSuccess(updatedUser)
      onClose()
    } catch (err) {
      console.error('Error updating user roles:', err)
      setError('Failed to update user roles. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  /**
   * Saves the switch, then re-reads this person's role set: the switch is stored on it, so its ETag has
   * moved, and the next role edit would otherwise be refused as a conflict.
   */
  const saveSwitch = async (savedUser: UserProfileWithRolesDto) => {
    setSaving(true)
    setError(null)
    let switchSaved = false
    try {
      const change = await switchAccess.save()
      switchSaved = true
      const refreshed = await userRolesApi.getUserCompanyRoleSet(user.UserProfileId, savedUser.UserCompanyRoleSet?.ClientCompanyId ?? '')
      if (refreshed.error || !refreshed.data) {
        // Without the re-read the ETag is the one the switch just used up, and the next role edit would be
        // refused. Stay open: Save again resends the switch, which changes nothing now, and re-reads.
        throw new Error('Access to all customers was saved, but the latest roles could not be loaded. Save again to finish.')
      }
      const updatedUser: UserProfileWithRolesDto = { ...savedUser, UserCompanyRoleSet: refreshed.data }

      const reached = change.Added.length + change.Replaced.length + change.Changed.length
      showSuccess(
        change.Enabled ? 'Access to all customers on' : 'Access to all customers off',
        change.Enabled
          ? `${user.FirstName} is ${change.Role} on ${reached} ${reached === 1 ? 'customer' : 'customers'}, and on every new one.`
          : `Switch-managed access removed from ${change.Removed.length} ${change.Removed.length === 1 ? 'customer' : 'customers'}.`
      )
      if (change.Skipped.length > 0) {
        showWarning('Some customers were skipped', `${user.FirstName} is the last admin at ${change.Skipped.join(', ')}, so their access there was kept.`, 10000)
      }

      onSuccess(updatedUser)
      onClose()
    } catch (err) {
      // The roles may have been saved just before this. Keep them as the new baseline, with their new
      // ETag, so Save retries only the switch instead of resending roles under an ETag already used.
      if (savedUser !== current) {
        setSavedRoleSet(savedUser.UserCompanyRoleSet ?? null)
        setOriginalCompanyRoles([...companyRoles])
        setOriginalWorkspaceRoles(JSON.parse(JSON.stringify(workspaceRoles)))
        // Once the switch has saved, savedUser's ETag is stale too, so the page waits for the re-read.
        if (!switchSaved) onSuccess(savedUser)
      }
      setError(err instanceof Error ? err.message : 'Access to all customers could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  const dirty = hasChanges() || switchAccess.dirty
  // The switch can change access on every customer, so it is not saved until its preview has said how.
  const switchUnpreviewed = switchAccess.dirty && !switchAccess.preview

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Slide-over panel */}
      <div className="fixed inset-y-0 right-0 max-w-full flex">
        <div className="w-screen max-w-4xl">
          <div className="h-full flex flex-col bg-white dark:bg-slate-900 shadow-xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <PersonAvatar
                      firstName={user.FirstName}
                      lastName={user.LastName}
                      profilePicUrl={user.AvatarUrl}
                      alt={`${user.FirstName} ${user.LastName}`}
                      size="md"
                    />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                      Edit {user.FirstName}'s Roles
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {user.Email}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="px-6 py-3 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
                <div className="flex items-start justify-between">
                  <p className="text-sm text-red-600 dark:text-red-400 flex-1 pr-2">{error}</p>
                  <button
                    onClick={() => setError(null)}
                    className="flex-shrink-0 p-1 text-red-400 hover:text-red-600 dark:hover:text-red-300 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Company override info */}
            <div className="px-6 py-3 bg-primary-50 dark:bg-primary-900/20 border-b border-primary-200 dark:border-primary-800">
              <div className="flex items-start space-x-2">
                <Info className="w-4 h-4 text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-primary-800 dark:text-primary-200">
                  <strong>Important:</strong> Company roles override workspace roles. If a user has a role at the company level, they automatically have that role in all workspaces.
                </p>
              </div>
            </div>

            {/* Content - RoleSelector */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Above the roles: below them it sat under every workspace and had to be scrolled to. */}
              {allCustomersAccess && (
                <AllCustomersAccessSection
                  access={switchAccess}
                  managingCompanyName={allCustomersAccess.managingCompanyName}
                  firstName={user.FirstName}
                />
              )}

              <RoleSelector
                companyRoles={companyRoles}
                workspaceRoles={workspaceRoles}
                selectedWorkspaces={selectedWorkspaces}
                companyWorkspaces={companyWorkspaces}
                onCompanyRoleChange={handleCompanyRoleChange}
                onWorkspaceRoleChange={handleWorkspaceRoleChange}
                collapsedWorkspaces={collapsedWorkspaces}
                onToggleWorkspaceCollapse={toggleWorkspaceCollapse}
                showEffectiveRoles={true}
              />
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end space-x-3">
              <button
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !dirty || switchUnpreviewed}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>
                  {saving ? 'Saving...' : dirty ? 'Save Changes' : 'No Changes'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
